import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import clsx from 'clsx';
import {Trans} from '@common/i18n/trans';
import {FormattedCurrency} from '@common/i18n/formatted-currency';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}

export function PricingTable({selectedCycle, className, productLoader}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div className={clsx('rp-pt', className)}>
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList key="plan-list" plans={query.data.products} selectedPeriod={selectedCycle} />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}

function calcSavingsPct(plan: Product): number {
  const monthly = findBestPrice('monthly', plan.prices);
  const yearly = findBestPrice('yearly', plan.prices);
  if (!monthly || !yearly) return 0;
  const pct = Math.round(((monthly.amount * 12 - yearly.amount) / (monthly.amount * 12)) * 100);
  return pct > 0 ? pct : 0;
}

/* Replit-style check icon */
function CheckIcon({plus}: {plus?: boolean}) {
  if (plus) {
    return (
      <svg className="rp-pcard__check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14M5 12h14"/>
      </svg>
    );
  }
  return (
    <svg className="rp-pcard__check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

/* Static Enterprise card */
const ENTERPRISE_FEATURES = [
  'Everything in Pro',
  'Custom seat limits',
  'SSO / SAML',
  'Advanced privacy controls',
  'Design system support',
  'Data warehouse connections',
  'Custom groups',
  'Dedicated support',
  'Single-tenant environments',
  'Region selection',
  'Static outbound IPs',
  'VPC peering',
];

function EnterpriseCard() {
  return (
    <div className="rp-pcard rp-pcard--enterprise">
      <div className="rp-pcard__top">
        <span className="rp-pcard__name">Enterprise</span>
      </div>

      <div className="rp-pcard__price-area">
        <div className="rp-pcard__amount-custom">Custom</div>
      </div>

      <p className="rp-pcard__desc">For enterprise-grade security &amp; controls</p>

      <hr className="rp-pcard__divider" />

      <ul className="rp-pcard__features">
        {ENTERPRISE_FEATURES.map(f => (
          <li key={f} className="rp-pcard__feature">
            <CheckIcon plus={f.startsWith('Everything')} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="rp-pcard__footer">
        <Link to="/contact" className="rp-pcard__cta rp-pcard__cta--ghost">
          Contact us
        </Link>
      </div>
    </div>
  );
}

function PlanList({plans, selectedPeriod}: PlanListProps) {
  const {isLoggedIn, isSubscribed} = useAuth();
  const {billing} = useSettings();
  const filteredPlans = plans.filter(plan => !plan.hidden);

  return (
    <Fragment>
      {filteredPlans.map(plan => {
        const price = findBestPrice(selectedPeriod, plan.prices);
        const monthlyPrice = findBestPrice('monthly', plan.prices);
        const savingsPct = calcSavingsPct(plan);

        let upgradeRoute: string | undefined;
        if (!isLoggedIn) upgradeRoute = `/register?redirectFrom=pricing`;
        if (isSubscribed) upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
        if (isLoggedIn && !plan.free) upgradeRoute = `/checkout/${plan.id}/${price?.id}`;

        const displayAmount = selectedPeriod === 'yearly' && price
          ? Math.round(price.amount / 12)
          : price?.amount;

        const currency = price?.currency ?? 'usd';

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx('rp-pcard', plan.recommended && 'rp-pcard--featured')}
          >
            {/* Plan name + discount badge */}
            <div className="rp-pcard__top">
              <span className="rp-pcard__name">
                <Trans message={plan.name} />
              </span>
              {savingsPct > 0 && selectedPeriod === 'yearly' && (
                <span className="rp-pcard__badge">
                  {savingsPct}% off
                </span>
              )}
            </div>

            {/* Price */}
            <div className="rp-pcard__price-area">
              {plan.free ? (
                <div className="rp-pcard__amount-free">Free</div>
              ) : price ? (
                <div className="rp-pcard__price-row">
                  {selectedPeriod === 'yearly' && monthlyPrice && savingsPct > 0 && (
                    <span className="rp-pcard__original">
                      <FormattedCurrency value={monthlyPrice.amount} currency={currency} />
                    </span>
                  )}
                  <span className="rp-pcard__amount">
                    <FormattedCurrency value={displayAmount ?? 0} currency={currency} />
                  </span>
                  <div className="rp-pcard__period-stack">
                    <span>per month</span>
                    {selectedPeriod === 'yearly' && price && (
                      <span>
                        <FormattedCurrency value={price.amount} currency={currency} /> / year
                      </span>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Description */}
            {plan.description && (
              <p className="rp-pcard__desc">
                <Trans message={plan.description} />
              </p>
            )}

            <hr className="rp-pcard__divider" />

            {/* Features */}
            {plan.feature_list.length > 0 && (
              <ul className="rp-pcard__features">
                {plan.feature_list.map((f, idx) => (
                  <li key={f} className="rp-pcard__feature">
                    <CheckIcon plus={idx === 0 && plan.recommended} />
                    <span><Trans message={f} /></span>
                  </li>
                ))}
              </ul>
            )}

            {/* CTA */}
            <div className="rp-pcard__footer">
              {plan.free ? (
                <Link to={upgradeRoute ?? '/register'} className="rp-pcard__cta rp-pcard__cta--ghost">
                  Sign up
                </Link>
              ) : (
                <Link
                  to={upgradeRoute ?? '#'}
                  className="rp-pcard__cta"
                  onClick={() => {
                    if (isLoggedIn || !price || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price.id,
                    });
                  }}
                >
                  Upgrade plan
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              )}
            </div>
          </m.div>
        );
      })}

      {/* Static Enterprise card always last */}
      <EnterpriseCard />
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      {[1, 2, 3, 4].map(i => (
        <m.div key={i} {...opacityAnimation} className="rp-pcard rp-pcard--skeleton">
          <Skeleton className="mb-12 h-16" />
          <Skeleton className="mb-8 h-40" />
          <Skeleton className="mb-20 h-14" />
          <Skeleton className="mb-10 h-1" />
          <Skeleton className="mb-8" />
          <Skeleton className="mb-8" />
          <Skeleton className="mb-8" />
          <Skeleton className="mt-auto h-40 rounded-full" />
        </m.div>
      ))}
    </Fragment>
  );
}
